<script setup>
/**
 * "Where this answer is kept" — the two boxes that replace the old "What to write down"
 * list. THE PLAN, decision 6.
 *
 * THEY ARE NOT ALTERNATIVES, and the sidecar's own comment is the reason (`types.ts`,
 * `SaveTarget`): a connection input can only name a CHAT key, so a booking that needed the
 * name it had just collected had to keep that name out of the customer's profile — and
 * lead capture silently stopped for every customer who booked. Filling both sends the
 * answer to the connection AND to the profile.
 *
 * The chat key is a machine name (snake_case) because it is what a connection's input
 * refers to, and it is the one box on this card an owner can put a space in and get a
 * refusal she cannot read. So it says what it is for, and the error under it names the
 * rule rather than the regular expression.
 */
import WasloInput from './WasloInput.vue';
import { useWasloI18n } from '../i18n.js';

defineProps({
  /** `{ chat, contact }`, both plain strings — see `helpers/task-flow-draft.js`. */
  modelValue: { type: Object, required: true },
  /** Known contact fields, e.g. `['name','email','phone_number']`. */
  contactFields: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useWasloI18n();

const key = name => `WASLO.TASKS.EDITOR.FLOW.SAVE.${name}`;
</script>

<template>
  <div class="flex flex-col gap-3" data-waslo-step-save>
    <p class="mb-0 text-sm font-medium text-n-slate-12">
      {{ t(key('HEADING')) }}
    </p>
    <p class="mb-0 text-sm text-n-slate-11">{{ t(key('DESCRIPTION')) }}</p>
    <div class="grid gap-3 md:grid-cols-2">
      <WasloInput
        :model-value="modelValue.chat"
        :label="t(key('CHAT_LABEL'))"
        :hint="t(key('CHAT_HINT'))"
        placeholder="delivery_area"
        data-waslo-step-save-chat
        @update:model-value="
          value => emit('update:modelValue', { ...modelValue, chat: value })
        "
      />
      <WasloInput
        :model-value="modelValue.contact"
        :label="t(key('CONTACT_LABEL'))"
        :hint="t(key('CONTACT_HINT'))"
        :placeholder="contactFields[0] || 'name'"
        data-waslo-step-save-contact
        @update:model-value="
          value => emit('update:modelValue', { ...modelValue, contact: value })
        "
      />
    </div>
    <p
      v-if="!modelValue.chat.trim() && !modelValue.contact.trim()"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-step-save-empty
    >
      {{ t(key('NEITHER')) }}
    </p>
  </div>
</template>
